import React, { useState } from "react";
import {
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
  AppBar,
  Toolbar,
  Drawer,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import MenuIcon from "@mui/icons-material/Menu";
import { useAuth } from "../hooks/useAuth";
import { usePermissions } from "../hooks/usePermissions";
import { useCompany } from "../hooks/useCompany";
import logoMark from "../assets/bestek-mark.png";
import { collapsedWidth, expandedWidth } from "./constantSizes";
import { RADIUS, TRANSITION } from "../theme/mixins";
import { Icon, type IconName } from "../components/ui/icons";

// Interface for menu items with optional related paths
interface MenuItem {
  text: string;
  iconName: IconName;
  path: string;
  screenName: string;
  relatedPaths?: string[];
  bypassPermission?: boolean; // Show item regardless of backend permission (use while screen is not yet registered)
}

const Sidebar: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { currentAccount, logout } = useAuth();
  const { canRead } = usePermissions();
  const { userRoleName } = useCompany();

  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isMobile = useMediaQuery(theme.breakpoints.down(1200));
  const navigate = useNavigate();
  const location = useLocation();

  // BESTEKAI section items
  const bestekaiItems: MenuItem[] = [
    {
      text: t("navigation.dashboard"),
      iconName: "dashboard",
      path: "/dashboard",
      screenName: "Dashboard",
    },
    {
      text: t("navigation.convert"),
      iconName: "convert",
      path: "/converteren",
      screenName: "Convertion",
      relatedPaths: ["/converteren", "/conversion-results"],
    },
    {
      text: t("navigation.functionalities"),
      iconName: "grid",
      path: "/functionaliteiten",
      screenName: "Functionalities",
      relatedPaths: ["/functionaliteiten"],
    },
    {
      text: t("navigation.technischeSchatting"),
      iconName: "receipt",
      path: "/technische-schatting",
      screenName: "TechnischeSchatting",
      relatedPaths: ["/technische-schatting"],
    },
  ];

  // INSTELLINGEN section items
  const settingsItems: MenuItem[] = [
    {
      text: t("navigation.users"),
      iconName: "users",
      path: "/users",
      screenName: "User",
    },
    {
      text: t("navigation.companies"),
      iconName: "building",
      path: "/companies",
      screenName: "Company",
      relatedPaths: ["/companies", "/configuration"],
    },
    {
      text: t("navigation.rolesPermissions"),
      iconName: "lock",
      path: "/roles",
      screenName: "Role",
    },
  ];

  // RESULTATEN section item (history-backed; available to any authenticated user)
  const resultsItems: MenuItem[] = [
    {
      text: t("navigation.results", "Resultaten"),
      iconName: "results",
      path: "/results",
      screenName: "Results",
      relatedPaths: ["/results"],
    },
  ];

  // Filter menu items based on permissions
  const filteredBestekaiItems = bestekaiItems.filter((item) =>
    item.bypassPermission || canRead(item.screenName)
  );

  const bestekaiItemsToRender = filteredBestekaiItems;

  // Filter settings items based on permissions and role
  // Hide "Gebruikers" (Users) and "Bedrijven" (Companies) for users with role name "User"
  const filteredSettingsItems = settingsItems.filter((item) => {
    // First check if user has read permission
    if (!canRead(item.screenName)) {
      return false;
    }

    // If user role is "User", hide Users and Companies menu items
    if (userRoleName?.toLowerCase() === "user") {
      if (item.screenName === "User" || item.screenName === "Company") {
        return false;
      }
    }

    return true;
  });

  // Generic function to check if a menu item should be selected
  const isItemSelected = (item: MenuItem): boolean => {
    const currentPath = location.pathname;

    // If item has relatedPaths, check if current path matches any of them
    if (item.relatedPaths && item.relatedPaths.length > 0) {
      if (item.relatedPaths.includes(currentPath)) return true;
      // configuration detail (/configuration/:id) keeps "Bedrijven" highlighted
      if (
        item.path === "/companies" &&
        currentPath.startsWith("/configuration")
      ) {
        return true;
      }
      return false;
    }

    // Default behavior: exact path match
    return currentPath === item.path;
  };

  // Handle logout and close mobile drawer
  const handleLogout = () => {
    if (isMobile) {
      setMobileOpen(false);
    }
    logout();
  };

  // Get user initials from name (first and last name)
  const getUserInitials = (name: string | undefined): string => {
    if (!name) return "U";

    const nameParts = name.trim().split(/\s+/);
    if (nameParts.length === 0) return "U";

    if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }

    const firstInitial = nameParts[0].charAt(0).toUpperCase();
    const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
    return `${firstInitial}${lastInitial}`;
  };

  // For mobile, always show expanded view (collapsed = false)
  const isExpanded = isMobile ? true : !collapsed;

  const navButton = (item: MenuItem) => {
    const selected = isItemSelected(item);
    return (
      <Box
        component="button"
        key={item.text}
        title={!isExpanded ? item.text : undefined}
        onClick={() => {
          navigate(item.path);
          if (isMobile) setMobileOpen(false);
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          width: "100%",
          padding: isExpanded ? "10px 12px" : "10px 0",
          justifyContent: isExpanded ? "flex-start" : "center",
          border: "none",
          borderRadius: `${RADIUS.button}px`,
          cursor: "pointer",
          fontFamily: "inherit",
          fontSize: 14,
          fontWeight: selected ? 700 : 500,
          transition: TRANSITION,
          background: selected ? theme.palette.surface.cyan : "transparent",
          // brand.icon is a mid-teal: fine as the selected pill's ink on the
          // light-cyan surface, but body-weight nav labels stay navy/tertiary.
          color: selected
            ? theme.palette.brand.navy
            : theme.palette.text.tertiary,
          "&:hover": selected
            ? { background: theme.palette.surface.cyan }
            : {
                background: theme.palette.background.neutral,
                color: theme.palette.brand.navy,
              },
        }}
      >
        <span style={{ display: "flex", width: 20, height: 20, flex: "none" }}>
          <Icon name={item.iconName} size={20} />
        </span>
        {isExpanded && <span style={{ flex: 1, textAlign: "left" }}>{item.text}</span>}
      </Box>
    );
  };

  const sectionLabel = (label: string) =>
    isExpanded ? (
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.08em",
          color: theme.palette.text.secondary,
          padding: "10px 10px 8px",
        }}
      >
        {label}
      </div>
    ) : (
      <div style={{ height: 16 }} />
    );

  const drawerContent = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: theme.palette.common.white,
      }}
    >
      {/* Header: logo on the left, collapse toggle on the right (same row when expanded) */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: isExpanded ? "space-between" : "center",
          height: 66,
          padding: "24px 16px 8px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <img src={logoMark} alt="de VDK Groep" style={{ height: 34, width: "auto", flex: "none", display: "block" }} />
          {isExpanded && (
            <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1 }}>
              <span style={{ color: theme.palette.brand.navy }}>Bestek</span>
              <span style={{ color: theme.palette.primary.main }}> AI</span>
            </div>
          )}
        </div>

        {!isMobile && isExpanded && (
          <IconButton
            size="small"
            onClick={() => setCollapsed(!collapsed)}
            sx={{
              flex: "none",
              width: 40,
              height: 40,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "12px",
            }}
          >
            <MenuOpenIcon />
          </IconButton>
        )}
      </div>

      {/* Nav sections */}
      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "6px 14px 14px" }}>
        {!isMobile && !isExpanded && (
          <div style={{ display: "flex", justifyContent: "center", padding: "10px 0" }}>
            <IconButton
              size="small"
              onClick={() => setCollapsed(!collapsed)}
              sx={{
                flex: "none",
                width: 40,
                height: 40,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "12px",
              }}
            >
              <MenuIcon />
            </IconButton>
          </div>
        )}

        {bestekaiItemsToRender.length > 0 && (
          <>
            {sectionLabel("BESTEK AI")}
            <nav style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {bestekaiItemsToRender.map(navButton)}
            </nav>
          </>
        )}

        {resultsItems.length > 0 && (
          <>
            {sectionLabel("RESULTATEN")}
            <nav style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {resultsItems.map(navButton)}
            </nav>
          </>
        )}

        {filteredSettingsItems.length > 0 && (
          <>
            {sectionLabel("INSTELLINGEN")}
            <nav style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {filteredSettingsItems.map(navButton)}
            </nav>
          </>
        )}
      </div>

      {/* Bottom user card */}
      <div style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: 12 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "11px",
            padding: "8px",
            borderRadius: "12px",
            transition: "background 150ms cubic-bezier(0.2,0,0,1)",
            "&:hover": { background: theme.palette.background.neutral },
          }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              flex: "none",
              borderRadius: 11,
              background: theme.palette.brand.navy,
              color: theme.palette.common.white,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: "-0.01em",
            }}
          >
            {getUserInitials(currentAccount?.name)}
          </div>
          {isExpanded && (
            <>
              <div style={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 13.5,
                    color: theme.palette.brand.navy,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {currentAccount?.name || "User"}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: theme.palette.text.secondary,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {currentAccount?.username || "user@company.com"}
                </div>
              </div>
              <Box
                component="button"
                aria-label={t("navigation.logout")}
                title={t("navigation.logout")}
                onClick={handleLogout}
                sx={{
                  flex: "none",
                  width: 34,
                  height: 34,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "none",
                  background: "transparent",
                  color: theme.palette.text.secondary,
                  borderRadius: `${RADIUS.inputSm}px`,
                  cursor: "pointer",
                  transition: TRANSITION,
                  "&:hover": {
                    background: theme.palette.surface.cyan,
                    color: theme.palette.brand.navy,
                  },
                }}
              >
                <Icon name="logout" size={18} />
              </Box>
            </>
          )}
        </Box>
      </div>
    </div>
  );

  const paperSx = {
    width: isMobile ? expandedWidth : collapsed ? collapsedWidth : expandedWidth,
    transition: "width 0.3s",
    overflowX: "hidden",
    boxSizing: "border-box",
    borderRight: `1px solid ${theme.palette.divider} !important`,
    backgroundColor: theme.palette.common.white,
  } as const;

  return (
    <>
      {/* AppBar with hamburger only on mobile */}
      {isMobile && (
        <AppBar
          position="fixed"
          color="default"
          elevation={0}
          sx={{
            backgroundColor: theme.palette.common.white,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Toolbar sx={{ gap: 1 }}>
            <IconButton size="small" edge="start" onClick={() => setMobileOpen(true)}>
              <MenuIcon />
            </IconButton>
            <div style={{ display: "flex", alignItems: "center", gap: 9, flexGrow: 1 }}>
              <img src={logoMark} alt="de VDK Groep" style={{ height: 28 }} />
              <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.02em" }}>
                <span style={{ color: theme.palette.brand.navy }}>Bestek</span>
                <span style={{ color: theme.palette.primary.main }}> AI</span>
              </div>
            </div>
          </Toolbar>
        </AppBar>
      )}

      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: isMobile ? expandedWidth : collapsed ? collapsedWidth : expandedWidth,
          flexShrink: 0,
          whiteSpace: "nowrap",
          [`& .MuiDrawer-paper`]: paperSx,
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
